import { Injectable } from '@angular/core';
import Decimal from 'decimal.js';
import { CONSTANTS } from '../../constants/constants';
import { UnitConstants } from '../../constants/unit';
import { QuantityInstruction } from '../../interfaces/quantity-instruction';
import { Unit } from '../../interfaces/unit';
import { StorageService } from '../storage/storage.service';

interface UnitConversionData {
  CONVERSION_FACTOR: number;
  UNIT: string;
}

@Injectable({
  providedIn: 'root',
})
export class UnitsService {
  localRef: string;
  units: { [key: string]: { [key: string]: string } };

  constructor(private storageService: StorageService) {
    this.localRef = 'units';
    this.units = storageService.get(this.localRef) ?? {};
  }

  getFieldUnit(formName: string, id: string, quantity: string): Unit {
    if (this.hasFieldReference(formName, id)) {
      const reference = this.units[formName][id];
      return { reference, value: UnitConstants[quantity][reference].UNIT };
    }
    return UnitConstants.DEFAULT[quantity];
  }

  transform(
    formName: string,
    id: string,
    quantity: string,
    unitRef: string,
    value: number,
    updateRefs = true,
    firstConversion = false,
  ): number {
    const prevUnitRef = this.getFieldReference(formName, id, quantity, firstConversion);
    const prevUnit = this.getUnitConfig(quantity, prevUnitRef);
    const currUnit = this.getUnitConfig(quantity, unitRef);
    const valueInDefaultUnit = prevUnit ? value * prevUnit.CONVERSION_FACTOR : value;
    const valueInCurrUnit = currUnit
      ? valueInDefaultUnit / currUnit.CONVERSION_FACTOR
      : valueInDefaultUnit;

    if (updateRefs) {
      this.updateReference(formName, id, unitRef);
    }

    /**
     * @description ignoring float point error
     */
    const abs = Math.abs(valueInCurrUnit);
    const absRound = Math.round(abs);
    const consideredValue =
      absRound > abs
        ? new Decimal(abs / absRound).toNumber()
        : new Decimal(absRound / abs).toNumber();
    return new Decimal(1).minus(consideredValue).toNumber() <= CONSTANTS.FLOAT_POINT_ERROR_MODULE
      ? +Math.round(valueInCurrUnit).toFixed()
      : valueInCurrUnit;
  }

  transformToDefault(
    formName: string,
    payload: any,
    fieldDefinitions: QuantityInstruction | any,
  ): any {
    if (payload instanceof Array) {
      payload = payload.map((item) => this.transformToDefault(formName, item, fieldDefinitions));
    } else if (payload instanceof Object) {
      Object.entries(payload).forEach(([key, val]) => {
        if (!this.isIgnoredField(key)) {
          if (val instanceof Object || payload instanceof Array) {
            payload[key] = this.transformToDefault(formName, val, fieldDefinitions);
          }

          if (val !== null && !isNaN(val as any) && fieldDefinitions[key]?.quantity) {
            const quantity = fieldDefinitions[key].quantity;
            const defaultUnitRef = this.getDefaultReference(quantity);

            payload[key] = this.transform(
              formName,
              key,
              quantity,
              defaultUnitRef,
              val as number,
              false,
            );
          }
        }
      });
    }

    return payload;
  }

  transformFromDefault(
    formName: string,
    payload: any,
    fieldDefinitions: any,
    parentFieldName: string = null,
  ): any {
    if (payload instanceof Array) {
      if (parentFieldName && fieldDefinitions[parentFieldName]?.quantity) {
        payload = payload.map((item) => {
          const objectMock = {};
          objectMock[parentFieldName] = item;
          const parsedValue = this.transformFromDefault(formName, objectMock, fieldDefinitions)[
            parentFieldName
          ];

          return parsedValue;
        });
        return payload;
      }

      payload = payload.map((item) => this.transformFromDefault(formName, item, fieldDefinitions));
      return payload;
    }

    if (payload instanceof Object) {
      Object.entries(payload).forEach(([key, val]) => {
        if (!this.isIgnoredField(key)) {
          if (val instanceof Object || payload instanceof Array) {
            payload[key] = this.transformFromDefault(formName, val, fieldDefinitions, key);
          }

          if (val !== null && !isNaN(val as any) && fieldDefinitions[key]?.quantity) {
            const quantity = fieldDefinitions[key].quantity;
            const fieldUnitRef = this.getFieldReference(formName, key, quantity);
            payload[key] = this.transform(
              formName,
              key,
              quantity,
              fieldUnitRef,
              val as number,
              false,
              true,
            );
          }
        }
      });

      return payload;
    }

    return payload;
  }

  getFieldReference(
    formName: string,
    id: string,
    quantity: string,
    forceDefault?: boolean,
  ): string {
    if (!forceDefault && this.hasFieldReference(formName, id)) {
      return this.units[formName][id];
    }
    return this.getDefaultReference(quantity);
  }

  getUnitConfig(quantity: string, unitRef: string): UnitConversionData {
    return UnitConstants[quantity] && UnitConstants[quantity][unitRef]
      ? UnitConstants[quantity][unitRef]
      : null;
  }

  getDefaultReference(quantity: string): string {
    return UnitConstants.DEFAULT[quantity]?.reference;
  }

  updateReference(formName: string, id: string, unitRef: string): void {
    const newFieldRefs = { [id]: unitRef };
    const newFormRefs = { [formName]: newFieldRefs };

    if (this.units[formName]) {
      this.units[formName] = Object.assign(this.units[formName], newFieldRefs);
    } else {
      this.units = Object.assign(this.units, newFormRefs);
    }

    this.storageService.set(this.localRef, this.units);
  }

  hasFieldReference(formName: string, id: string): boolean {
    return !!(this.units && this.units[formName] && this.units[formName][id]);
  }

  isIgnoredField(fieldName: string): boolean {
    return (
      fieldName === CONSTANTS.GRAPHIC_PROPERTIES ||
      fieldName === CONSTANTS.ID ||
      fieldName === CONSTANTS.INTERNAL_ID ||
      fieldName === CONSTANTS.NEEDS_UPDATE ||
      fieldName === CONSTANTS.TITLE
    );
  }
}
